import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { Label } from "@/components/ui/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { OctagonX } from "lucide-react";
import { useState } from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { useSignUp } from "../../hooks/dashboard/useSignUp";
import { api } from "@/utils/api";
import { useAuth } from "@/context/authContext";
import { MultiSessionConfirmDialog } from "@/artifacts/student/components/dashboard/OpenSignups/MultiSessionConfirmDialog";

const schema = z.object({
  explorationId: z
    .string("Exploration is required")
    .min(1, "Exploration is required"),
});

export type SingletonSignUpFormFields = z.infer<typeof schema>;

const SingletonSignUpForm = ({
  data,
  loading,
  onSuccess,
}: {
  data: {
    explorations: any[];
    sessionId: string;
  };
  loading: boolean;
  onSuccess: (sessionId: string) => void;
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SingletonSignUpFormFields>({
    resolver: zodResolver(schema),
  });

  const { signUp } = useSignUp();
  const { dispatch } = useAuth();

  const [confirm, setConfirm] = useState<{
    open: boolean;
    explorationId: string;
    exploration: any;
    siblings: any[];
    sessions: any[];
    rotations: any[];
  }>({
    open: false,
    explorationId: "",
    exploration: null,
    siblings: [],
    sessions: [],
    rotations: [],
  });

  const submitSignUp = async (explorationId: string) => {
    const result = await signUp(data.sessionId, explorationId);
    if (result?.session) onSuccess(result.session);
  };

  const onSubmit: SubmitHandler<SingletonSignUpFormFields> = async (
    formData,
  ) => {
    const exploration = data.explorations.find(
      (e) => e._id === formData.explorationId,
    );

    if (exploration?.multiSessionGroup) {
      try {
        const response = await api(
          `/explorations/multi-session/${formData.explorationId}`,
          { method: "GET" },
          dispatch,
        );
        setConfirm({
          open: true,
          explorationId: formData.explorationId,
          exploration,
          siblings: response.siblings ?? [],
          sessions: response.sessions ?? [],
          rotations: response.rotations ?? [],
        });
        return;
      } catch {
        // Fall through to direct signup if siblings fetch fails
      }
    }

    await submitSignUp(formData.explorationId);
  };

  return (
    <>
      <SheetBody>
        <form id="singleton-sign-up-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Exploration</Label>
                {errors.explorationId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.explorationId.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              {loading ? (
                <Skeleton style={{ height: "36px", width: "100%" }} />
              ) : (
                <Controller
                  control={control}
                  name="explorationId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v)}
                    >
                      <SelectTrigger aria-invalid={!!errors.explorationId}>
                        <SelectValue placeholder="Select an exploration" />
                      </SelectTrigger>
                      <SelectContent>
                        {data.explorations.map((e) => (
                          <SelectItem key={e._id} value={e._id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
            </VStack>
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="singleton-sign-up-form"
          style={{ width: "100%" }}
        >
          Sign Up
        </Button>
        <SheetClose asChild>
          <Button variant="outline" style={{ width: "100%" }}>
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
      <MultiSessionConfirmDialog
        open={confirm.open}
        onOpenChange={(v) => setConfirm((prev) => ({ ...prev, open: v }))}
        onConfirm={async () => {
          setConfirm((prev) => ({ ...prev, open: false }));
          await submitSignUp(confirm.explorationId);
        }}
        exploration={confirm.exploration ?? { name: "" }}
        siblings={confirm.siblings}
        sessions={confirm.sessions}
        rotations={confirm.rotations}
        variant="singleton"
      />
    </>
  );
};

export { SingletonSignUpForm };
